/**
 * Which mailbox the Email view shows: Gmail, or the user's linked Microsoft
 * 365 mailbox. Once Microsoft 365 is linked it replaces Gmail in the Email
 * view (Gmail stays connected for Drive and Calendar).
 *
 * Module state, not context, so services and hooks outside the React tree
 * read the same answer. It starts as Gmail and is settled by
 * resolveMailProvider(); screens that must not flash the wrong mailbox wait
 * for `resolved`.
 */
import { useSyncExternalStore } from 'react';
import { OutlookService } from '../outlookService';
import type { MailProviderId } from './mailCapabilities';

export type { MailProviderId };

export type MailProviderState = {
  userId: string | null;
  provider: MailProviderId;
  /** Settled for this user. False until the first status read, or after it failed. */
  resolved: boolean;
  /** The last status read failed, so `provider` is a fallback worth re-checking. */
  uncertain: boolean;
};

let state: MailProviderState = { userId: null, provider: 'gmail', resolved: false, uncertain: false };
const listeners = new Set<() => void>();
const inFlight = new Map<string, Promise<MailProviderId>>();

function publish(next: MailProviderState): void {
  if (next.userId === state.userId && next.provider === state.provider &&
      next.resolved === state.resolved && next.uncertain === state.uncertain) return;
  state = next;
  for (const fn of listeners) fn();
}

export function getMailProviderState(): MailProviderState {
  return state;
}

export function getActiveMailProvider(): MailProviderId {
  return state.provider;
}

export function subscribeMailProvider(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Reads the user's Outlook link and settles the provider. Never throws: a failed read keeps Gmail and marks it uncertain. */
export function resolveMailProvider(userId: string): Promise<MailProviderId> {
  const pending = inFlight.get(userId);
  if (pending) return pending;
  if (state.userId !== userId) publish({ userId, provider: 'gmail', resolved: false, uncertain: false });
  const run = OutlookService.status()
    .then((status) => {
      const provider: MailProviderId = status.connected ? 'microsoft' : 'gmail';
      if (state.userId === userId) publish({ userId, provider, resolved: true, uncertain: false });
      return provider;
    })
    .catch(() => {
      if (state.userId === userId) publish({ userId, provider: state.provider, resolved: true, uncertain: true });
      return state.provider;
    })
    .finally(() => inFlight.delete(userId));
  inFlight.set(userId, run);
  return run;
}

/** Sign-out, or a different user signing in on this browser. */
export function resetMailProvider(): void {
  publish({ userId: null, provider: 'gmail', resolved: false, uncertain: false });
}

export function useMailProvider(): MailProviderState {
  return useSyncExternalStore(subscribeMailProvider, getMailProviderState, getMailProviderState);
}

/**
 * Realtime events on the user's mail channel name their provider; Gmail's
 * predate that and carry none. Anything not from the shown mailbox is ignored.
 */
export function isEventForProvider(payload: unknown, provider: MailProviderId): boolean {
  const named = (payload as { provider?: unknown } | null | undefined)?.provider;
  return (named === 'microsoft' ? 'microsoft' : 'gmail') === provider;
}
